import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Preserve the originally requested destination (safe internal path only)
  const raw = searchParams.get("redirect") || "/app";
  const destination = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/app";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(destination);
    } catch (err) {
      const msg = apiError(err);
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fadeUp">
        <Link to="/" className="mb-8 flex flex-col items-center gap-2">
          <img src="/spora1-logo.webp" alt="SPORA1" className="h-12 w-auto" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            One Ecosystem <span className="text-primary">•</span> One Technology <span className="text-primary">•</span> Many Champions
          </p>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-display text-3xl font-bold">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Access your SPORA1 dashboard</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Email or username</label>
              <input data-testid="login-email-input" type="text" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="you@institution.edu or username" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Password</label>
              <input data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 font-semibold text-primary-foreground transition-all hover:bg-[#FF8533] disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            New here? <Link to="/register" className="font-medium text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
